import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AdvanceSearchService } from '../../../services/Advance-search/advance-search.service';
import { ToastService } from '../../../services/toast/toast.service';
import { MatSelectModule } from "@angular/material/select";
import { PincodeLookupController } from '../../../common-files/pincode-lookup';
import { Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { buildAccommodationAgreement } from '../../../common-files/accommodation-agreement';

@Component({
  selector: 'app-edit-devotee-fc',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatSelectModule],
  templateUrl: './edit-devotee-fc.component.html',
  styleUrl: './edit-devotee-fc.component.scss',
})
export class EditDevoteeFcComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() isAccntExist: boolean = false;
  @Input() devoteeDetails: any = {};
  @Input() updatedFc!: string;
  @Input() newMemberCode!: string;
  @Input() updateMember!: string;
  @Input() profileList: any[] = [];
  @Output() close = new EventEmitter<boolean>();
  maleRelations: string[] = [
    'Father',
    'Son',
    'Brother',
    'Husband',
    'Son-In-Law',
    'Brother-In-Law',
    'Grand-Father',
    'Grand-Son',
    'Uncle',
    'Cousin',
    'Relative Others',
    'GuruBhai Attached with Mandir',
    'GuruBhai Others',
    'Non-Initiated Male',
  ];

  femaleRelations: string[] = [
    'Mother',
    'Daughter',
    'Sister',
    'Wife',
    'Daughter-In-Law',
    'Sister-In-Law',
    'Grand-Mother',
    'Grand-Daughter',
    'Aunt',
    'Cousin',
    'Relative Others',
    'GuruBhai Attached with Mandir',
    'GuruBhai Others',
    'Non-Initiated Female',
  ];

  step: number = 0;
  contactNumbers: any[] = [];
  addressDetails: any[] = [];
  selectedNumber: string = '';
  isValidNumber = false;
  errorMessage = '';
  isCheckingNumber = false;
  isLoadingContacts = false;
  isSubmitting = false;
  addressfields!: FormGroup;
  agreementDetails!: FormGroup;
  pincodeLookup!: PincodeLookupController;
  steppers: any[] = [];
  isRelation: boolean = false;
  selectedRelation: string = '';
  importChecked: boolean = false;
  private initialized = false;
  private contactNumbersLoadedFor: string | null = null;
  private contactNumbersRequest?: Subscription;
  private addressRequest?: Subscription;
  private availabilityRequest?: Subscription;
  private submitRequest?: Subscription;
  private readonly destroy$ = new Subject<void>();

  onImportChange(): void {
    if (this.importChecked) {
      this.importAddress();
    } else {
      const address = this.addressDetails[0];
      this.patchSavedAddress(address);
    }
  }

  constructor(
    private advanceService: AdvanceSearchService,
    private fb: FormBuilder,

    private toaster: ToastService,
  ) {}

  ngOnInit(): void {
    this.addressfields = this.fb.group({
      houseNo: ['', Validators.required],
      locality: ['', Validators.required],
      landmark: [''],
      city: ['', Validators.required],
      pincode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      district: [{ value: '', disabled: true }, Validators.required],
      state: [{ value: '', disabled: true }, Validators.required],
      country: [{ value: '', disabled: true }, Validators.required],
    });

    this.pincodeLookup = new PincodeLookupController(
      this.addressfields,
      this.advanceService,
    );
    this.pincodeLookup.start();

    this.agreementDetails = this.fb.group({
      fullName: ['', Validators.required],
      guardianName: ['', Validators.required],
      place: ['', Validators.required],
      relation: [false, Validators.required],
    });
    this.initialized = true;
    if (this.isOpen) this.initializeFlow();
  }

  ngOnDestroy(): void {
    this.cancelFlowRequests();
    this.pincodeLookup?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get districtList(): string[] {
    return this.pincodeLookup?.districtList ?? [];
  }

  onDistrictChange(): void {
    this.pincodeLookup.resolveSelectedDistrict();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isOpen || !this.initialized) return;
    if (
      changes['isOpen'] ||
      changes['devoteeDetails'] ||
      changes['isAccntExist'] ||
      changes['updatedFc'] ||
      changes['newMemberCode']
    ) {
      this.initializeFlow();
    }
  }

  get isContactOnlyFlow(): boolean {
    return !this.isAccntExist && Boolean(this.devoteeDetails?.isPrimaryDevotee);
  }

  get isSubmitDisabled(): boolean {
    if (this.isSubmitting || this.isCheckingNumber || this.isLoadingContacts) return true;
    if (this.isRelation) return !this.selectedRelation;
    return !this.selectedNumber || !this.isValidNumber;
  }

  private initializeFlow(): void {
    this.resetFlowState();
    if (this.isAccntExist) {
      this.steppers = ['Choose Relations'];
      this.isRelation = true;
      return;
    }

    if (this.devoteeDetails?.isPrimaryDevotee) {
      this.steppers = ['Change Contact NO.'];
      this.getContactNumbers();
      return;
    }

    this.steppers = [
      'Address details',
      'Agreement to guidelines(on behalf of the requestor)',
      'Change Contact NO.',
    ];
    this.getDevoteeAddress();
  }

  private resetFlowState(): void {
    this.step = 0;
    this.selectedRelation = '';
    this.selectedNumber = '';
    this.errorMessage = '';
    this.isValidNumber = false;
    this.isCheckingNumber = false;
    this.isLoadingContacts = false;
    this.isSubmitting = false;
    this.isRelation = false;
    this.importChecked = false;
    this.contactNumbers = [];
    this.addressDetails = [];
    this.contactNumbersLoadedFor = null;
    this.cancelFlowRequests();

    this.addressfields?.reset({
      houseNo: '',
      locality: '',
      landmark: '',
      city: '',
      pincode: '',
      district: { value: '', disabled: true },
      state: { value: '', disabled: true },
      country: { value: '', disabled: true },
    });
    this.agreementDetails?.reset({
      fullName: '',
      guardianName: '',
      place: '',
      relation: false,
    });
  }

  onClose() {
    this.close.emit(false);
  }

  getRelations(member: any): string[] {
    if (!member?.gender) return [];

    return member.gender.toLowerCase() === 'male'
      ? this.maleRelations
      : this.femaleRelations;
  }

  getContactNumbers() {
    const familyCode = String(this.updatedFc ?? '');
    if (
      !familyCode
      || this.contactNumbersLoadedFor === familyCode
      || (this.contactNumbersRequest && !this.contactNumbersRequest.closed)
    ) {
      return;
    }

    this.isLoadingContacts = true;
    this.contactNumbersRequest = this.advanceService.fetchContactNumbers(familyCode)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.isLoadingContacts = false; }),
      )
      .subscribe({
      next: (res: any) => {
        this.contactNumbers = res?.data.contactNumbers || [];
        this.contactNumbersLoadedFor = familyCode;
      },
      error: (err) => {
        this.toaster.error(err.message);
      },
    });
  }

  incrementStep() {
    if (this.steppers.length !== 3) return;

    if (this.step === 0) {
      if (!this.hasValidAddressLocation()) return;
      this.prepareAgreement();
      this.step = 1;
      return;
    }

    if (this.step === 1) {
      if (this.agreementDetails.invalid) {
        this.agreementDetails.markAllAsTouched();
        return;
      }
      this.step = 2;
      this.getContactNumbers();
    }
  }

  decrementStep() {
    this.step = Math.max(0, this.step - 1);
  }

  getPrimaryRelation(val: string) {
    this.isValidNumber = Boolean(val);
  }

  onNumberChange(): void {
    this.availabilityRequest?.unsubscribe();
    if (!this.selectedNumber) {
      this.isValidNumber = false;
      this.isCheckingNumber = false;
      this.errorMessage = '';
      return;
    }

    const requestedNumber = this.selectedNumber;
    this.isCheckingNumber = true;
    this.isValidNumber = false;
    this.errorMessage = '';

    this.availabilityRequest = this.advanceService
      .fetchAvailableContactNumber({
        contactNumber: requestedNumber,
        familyCode: this.updatedFc,
        isPrimaryDevotee: this.devoteeDetails.isPrimaryDevotee,
        oldFamilyCode: this.devoteeDetails.devoteeFamilyCode,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.selectedNumber === requestedNumber) this.isCheckingNumber = false;
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (this.selectedNumber !== requestedNumber) return;

          if (res?.success === true && res?.data?.available === true) {
            this.isValidNumber = true;
            this.errorMessage = '';
          } else {
            this.isValidNumber = false;
            this.errorMessage = res?.message || 'Contact number already exists';
          }
        },
        error: (err: any) => {
          if (this.selectedNumber !== requestedNumber) return;
          this.isValidNumber = false;
          this.errorMessage = err?.error?.message || err?.message || '';
          this.toaster.error(this.errorMessage);
        },
      });
  }

  getDevoteeAddress() {
    const devoteeId = this.devoteeDetails?.devoteeId;
    if (!devoteeId || (this.addressRequest && !this.addressRequest.closed)) return;

    this.addressRequest = this.advanceService
      .fetchDevoteeAddress(this.devoteeDetails?.devoteeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.addressDetails = res?.data || [];
          const address = this.addressDetails[0];
          if (!address) {
            this.toaster.error('No address found');
            return;
          }

          this.patchSavedAddress(address);
        },
        error: (err) => {
          this.toaster.error(err.message);
        },
      });
  }

  importAddress(): void {
    const members: any[] = Object.values(this.profileList).flat();
    const primaryDevotee = members.find((member: any) => member.isPrimaryDevotee === true);

    if (!primaryDevotee) {
      this.toaster.error('No primary devotee found');
      return;
    }

    this.advanceService
      .fetchDevoteeAddressbyDevoteeId(primaryDevotee.devoteeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const address = res?.data?.[0];
          if (!address) {
            this.toaster.error('No address found');
            return;
          }

          this.patchSavedAddress(address);
          this.toaster.success('Address imported successfully');
        },
        error: (err) => {
          this.toaster.error(
            err?.error?.message || err?.message || 'Failed to import address',
          );
        },
      });
  }

  finalSubmit() {
    if (this.isSubmitting) return;
    if (this.isRelation && !this.selectedRelation) {
      this.toaster.error('Select a relationship before submitting.');
      return;
    }
    if (!this.isRelation && (!this.selectedNumber || !this.isValidNumber)) {
      this.toaster.error('Select a valid contact number before submitting.');
      return;
    }

    const updatesAddress = !this.isAccntExist && !this.devoteeDetails?.isPrimaryDevotee;
    if (updatesAddress && !this.hasValidAddressLocation()) {
      return;
    }

    let payload = {};
    if (!this.isAccntExist && !this.devoteeDetails?.isPrimaryDevotee) {
      const addressData = this.addressfields.getRawValue();
      payload = {
        devoteeId: this.devoteeDetails.devoteeId,
        familyCode: this.devoteeDetails.devoteeFamilyCode,
        newFamilyCode: this.updatedFc,
        newMemberCode: this.newMemberCode,
        gender: this.devoteeDetails.gender,
        contactNumber: this.selectedNumber,
        relationship: 'SELF',
        addressLine1: addressData.houseNo,
        addressLine2: addressData.locality,
        addressLine3: addressData.landmark,
        city: addressData.city,
        pin: addressData.pincode,
        state: addressData.state,
        district: addressData.district,
        agreementContent: buildAccommodationAgreement({
          fullName: this.agreementDetails.get('fullName')?.value,
          relationship: this.agreementDetails.get('relation')?.value,
          guardianName: this.agreementDetails.get('guardianName')?.value,
          place: this.agreementDetails.get('place')?.value,
        }),
        place: this.agreementDetails.get('place')?.value,
      };
    } else if (!this.isAccntExist && this.devoteeDetails?.isPrimaryDevotee) {
      payload = {
        devoteeId: this.devoteeDetails.devoteeId,
        familyCode: this.devoteeDetails.devoteeFamilyCode,
        newFamilyCode: this.updatedFc,
        newMemberCode: this.newMemberCode,
        contactNumber: this.selectedNumber,
        gender: this.devoteeDetails.gender,
        relationship: 'SELF',
      };
    } else if (
      (this.isAccntExist && this.devoteeDetails?.isPrimaryDevotee) ||
      (this.isAccntExist && !this.devoteeDetails?.isPrimaryDevotee)
    ) {
      payload = {
        devoteeId: this.devoteeDetails.devoteeId,
        familyCode: this.devoteeDetails.devoteeFamilyCode,
        newFamilyCode: this.updatedFc,
        newMemberCode: this.newMemberCode,
        // contactNumber: this.selectedNumber,
        gender: this.devoteeDetails.gender,
        relationship: this.selectedRelation,
      };
    }
    this.isSubmitting = true;
    this.submitRequest = this.advanceService.UpdatePrimaryDevotee(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => { this.isSubmitting = false; }),
      )
      .subscribe({
      next: (res) => {
        this.close.emit(false);
        this.toaster.success('Family code successfully updated');
      },
      error: (err) => {
        this.toaster.error(err.error.message);
      },
    });
  }

  private prepareAgreement(): void {
    const fullName = [
      this.devoteeDetails?.devoteeFirstName,
      this.devoteeDetails?.devoteeMiddleName,
      this.devoteeDetails?.devoteeLastName,
    ].filter(Boolean).join(' ');
    this.agreementDetails.patchValue({
      fullName,
      place: this.addressfields.get('city')?.value,
    });
  }

  private patchSavedAddress(address: any): void {
    this.addressfields.patchValue({
      houseNo: address?.addressLine1 || '',
      locality: address?.addressLine2 || '',
      landmark: address?.addressLine3 || '',
      city: address?.city || '',
      pincode: address?.pin || '',
      district: address?.district || '',
      state: address?.state || '',
      country: address?.country || '',
    }, { emitEvent: false });
    this.pincodeLookup.syncFromForm();
  }

  private cancelFlowRequests(): void {
    this.contactNumbersRequest?.unsubscribe();
    this.addressRequest?.unsubscribe();
    this.availabilityRequest?.unsubscribe();
    this.submitRequest?.unsubscribe();
    this.contactNumbersRequest = undefined;
    this.addressRequest = undefined;
    this.availabilityRequest = undefined;
    this.submitRequest = undefined;
  }

  private hasValidAddressLocation(): boolean {
    const address = this.addressfields.getRawValue();
    const isLocationVerified = this.pincodeLookup.isVerified(address.pincode);
    const hasResolvedLocation = Boolean(address.state && address.district);

    if (this.addressfields.invalid || !isLocationVerified || !hasResolvedLocation) {
      this.addressfields.markAllAsTouched();
      if (!isLocationVerified || !hasResolvedLocation) {
        this.toaster.error(
          this.pincodeLookup.message || 'Enter a valid, verified 6-digit pincode.',
        );
      }
      return false;
    }

    return true;
  }
}
